import { useEffect, useState } from "react";
import { AlertCircle, BookMarked, Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import AllergenMasterForm from "@/components/allergen/AllergenMasterForm";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AM } from "@/constants/testIds";
import { listAllergenMaster, setAllergenMasterActive } from "@/lib/allergenMasterApi";
import { ALLERGEN_TYPES, allergenTypeLabel } from "@/lib/allergyLookup";
import "@/styles/emr-catalog.css";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((row) => row.msg).join("; ");
  return "Unable to load Allergen Master.";
}

function identifierLabel(row) {
  if (row.code) return row.codeDisplay ? `${row.code} · ${row.codeDisplay}` : row.code;
  if (row.rxnormCode) return `RxNorm ${row.rxnormCode}`;
  if (row.snomedCode) return `SNOMED ${row.snomedCode}`;
  return "—";
}

export default function AllergenMasterPage() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [allergenType, setAllergenType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async ({ q, statusFilter, typeFilter } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const catalog = await listAllergenMaster({
        q: (q ?? query).trim() || undefined,
        allergenType: (typeFilter ?? allergenType) === "all" ? undefined : (typeFilter ?? allergenType),
        active:
          (statusFilter ?? status) === "all"
            ? null
            : (statusFilter ?? status) === "active",
      });
      setItems(catalog.items || []);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handle = setTimeout(
      () => load({ q: query, statusFilter: status, typeFilter: allergenType }),
      300,
    );
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce search/filter
  }, [query, status, allergenType]);

  return (
    <div className="emr-catalog emr-catalog--full-table" data-testid={AM.page}>
      <div className="emr-catalog__pagehead">
        <div>
          <h1 className="emr-catalog__title" data-testid={AM.title}>
            Allergen Master
          </h1>
          <p className="emr-catalog__subtitle">
            System allergen / contraindication terminology. Not a patient allergy list.
          </p>
        </div>
        <Button
          type="button"
          data-testid={AM.newBtn}
          className="btn emr-catalog__btn"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <Plus className="emr-catalog__btn-icon" />
          Add Allergen
        </Button>
      </div>

      <div className="emr-catalog__workspace">
        <section
          data-testid={AM.banner}
          className="card emr-catalog__section emr-catalog__section--notice"
        >
          <h2 className="emr-catalog__section-title">Reference data</h2>
          <p className="emr-catalog__copy">
            Complete catalog from <span className="font-fhir">allergen_master</span>. Inactive
            rows stay on existing patient allergy records and are hidden from new Allergy Lookup.
          </p>
        </section>
        <section className="card emr-catalog__section">
          <h2 className="emr-catalog__section-title">Search &amp; filters</h2>
          <div className="emr-catalog__filters emr-catalog__filters--search-row">
              <div className="emr-catalog__search">
                <Search className="emr-catalog__search-icon" aria-hidden="true" />
                <input
                  data-testid={AM.search}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search name, synonym, or code"
                  className="emr-catalog__input"
                />
              </div>
              <div className="emr-catalog__field">
                <span>Status</span>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid={AM.statusFilter} className="emr-catalog__filter-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="emr-catalog-select">
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="emr-catalog__field">
                <span>Allergen type</span>
                <Select value={allergenType} onValueChange={setAllergenType}>
                  <SelectTrigger data-testid={AM.typeFilter} className="emr-catalog__filter-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="emr-catalog-select">
                    {ALLERGEN_TYPES.map((row) => (
                      <SelectItem key={row.id || "all"} value={row.id || "all"}>
                        {row.id ? row.label : "All types"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
        </section>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">Allergens</h2>
          {error ? (
            <div data-testid={AM.errorState} className="emr-catalog__error">
              <AlertCircle className="emr-catalog__error-icon" aria-hidden="true" />
              <p className="emr-catalog__error-copy">{error}</p>
              <Button variant="outline" className="btn emr-catalog__retry" onClick={() => load()}>
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div data-testid={AM.loading} className="emr-catalog__status-msg">
              Loading Allergen Master…
            </div>
          ) : !items.length ? (
            <div data-testid={AM.emptyState} className="emr-catalog__empty">
              <BookMarked className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No allergens match this search.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={AM.table} className="striped highlight emr-catalog__table emr-catalog__table--wide">
                <thead>
                  <tr>
                    <th>Display name</th>
                    <th>Type</th>
                    <th>Identifier / code</th>
                    <th>Synonyms</th>
                    <th>Active</th>
                    <th className="emr-catalog__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} data-testid={`${AM.row}-${row.id}`}>
                      <td className="emr-catalog__name">{row.displayName}</td>
                      <td className="emr-catalog__muted">
                        {row.allergenTypeLabel || allergenTypeLabel(row.allergenType)}
                      </td>
                      <td className="font-fhir emr-catalog__muted">{identifierLabel(row)}</td>
                      <td className="emr-catalog__muted">
                        {(row.synonyms || []).join(", ") || "—"}
                      </td>
                      <td>
                        {row.active !== false ? (
                          <span className="emr-catalog__status emr-catalog__status--active">Yes</span>
                        ) : (
                          <span className="emr-catalog__status emr-catalog__status--inactive">No</span>
                        )}
                      </td>
                      <td>
                        <div className="emr-catalog__row-actions">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="emr-catalog-icon-btn"
                            data-testid={`${AM.editBtn}-${row.id}`}
                            onClick={() => {
                              setEditing(row);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <button
                            type="button"
                            className="emr-catalog__text-btn"
                            data-testid={`${AM.activateBtn}-${row.id}`}
                            onClick={async () => {
                              try {
                                await setAllergenMasterActive(row.id, row.active === false);
                                toast.success(row.active === false ? "Activated." : "Deactivated.");
                                load();
                              } catch (err) {
                                toast.error(apiErrorMessage(err));
                              }
                            }}
                          >
                            {row.active === false ? "Activate" : "Deactivate"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <AllergenMasterForm
        open={formOpen}
        editing={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => load()}
      />
    </div>
  );
}
